import {
  CalendarDotsIcon,
  CheckCircleIcon,
  CopyIcon,
  MapPinIcon,
} from "@phosphor-icons/react";
import dayjs from "dayjs";
import { useState } from "react";
import { useIntl } from "react-intl";

import { ButtonBase } from "@/components/ButtonBase/ButtonBase";
import { Chip } from "@/components/Chip/Chip";
import { Divider } from "@/components/Divider/Divider";
import {
  DrawerContent,
  DrawerTopBar,
  SwipeableDrawer,
} from "@/components/Drawer";
import { IconCheck, IconTraveller, IconTravellers } from "@/components/Icons";
import { CaretRightIcon } from "@/components/Icons/rtlReadyIcons";
import { LazyImage } from "@/components/Image/LazyImage";
import { ReservationDocumentList } from "@/features/bookings/components/ReservationDocumentList";
import { formatHotelName } from "@/features/hotels/utils/names";
import { TravellerDetailsCard } from "@/features/travellers/components/TravellerDetailsCard";
import type {
  FindHotelsHotelItemV2,
  FindOrderByIdAndUserIdOutputData,
  FindUserReservationsExternalHotelReservation,
  FindUserTravelersTravelers,
} from "@/types/codegen";
import { copyToClipboardWithFeedback } from "@/utils/copyToClipboard";
import { getAgeFromDateOfBirth } from "@/utils/getAgefromDateOfBirth";

type Props = {
  reservation: FindUserReservationsExternalHotelReservation;
  hotel: FindHotelsHotelItemV2;
  travellers: FindUserTravelersTravelers[];
  leadTravellerId: string | null;
  orderData: FindOrderByIdAndUserIdOutputData;
};

export const BookedHotelCard = ({
  hotel,
  reservation,
  travellers,
  leadTravellerId,
  orderData,
}: Props) => {
  const intl = useIntl();
  const [travellersDrawerVisible, setTravellersDrawerVisible] = useState(false);
  const [selectedTraveller, setSelectedTraveller] =
    useState<FindUserTravelersTravelers | null>(null);

  const startDate = dayjs(reservation.serviceDates.startDate.split("T")[0]);
  const endDate = dayjs(reservation.serviceDates.endDate.split("T")[0]);
  const numberOfDays = endDate.diff(startDate, "days") + 1;

  return (
    <>
      <div className="section-card">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="me-4 space-y-0.5">
              <div className="text-xl font-semibold text-text-primary">
                {intl.formatMessage({
                  defaultMessage: "Booking is confirmed!",
                  description:
                    "Title of the booked card on booking success screen",
                })}
              </div>
              <div className="text-sm text-text-tertiary">
                {formatHotelName(reservation.hotel.name)}
              </div>
            </div>
            <div className="relative">
              <div className="flex h-22.5 w-20 shrink-0 items-center overflow-clip rounded-lg bg-gray-100 text-center text-xs text-text-tertiary">
                {reservation.hotel.mainImageUrl ? (
                  <LazyImage
                    className="h-full w-full object-cover"
                    sentryElementClassName="self-start"
                    src={reservation.hotel.mainImageUrl}
                    alt={reservation.hotel.name}
                  />
                ) : (
                  <span className="grow">
                    {intl.formatMessage({
                      defaultMessage: "No image available",
                      description:
                        "Fallback alt text shown when no hotel or room image is available",
                    })}
                  </span>
                )}
              </div>
              <IconCheck className="absolute -start-4 -bottom-1 size-8 rounded-full bg-white" />
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex gap-2">
              <CalendarDotsIcon className="size-5 shrink-0" />
              <div className="space-y-1">
                <div className="font-medium text-text-primary">
                  {intl.formatMessage(
                    {
                      defaultMessage:
                        "{checkInDate, date, ::EEEMMMd} - {checkOutDate, date, ::EEEMMMd}",
                      description: "Formatted check in and checkout date",
                    },
                    {
                      checkInDate: startDate.toDate(),
                      checkOutDate: endDate.toDate(),
                    },
                  )}
                </div>
                <div className="text-sm text-text-tertiary">
                  {intl.formatMessage(
                    {
                      defaultMessage:
                        "{daysCount, plural, one {# Day} other {# Days}} - {nightsCount, plural, one {# Night} other {# Nights}}",
                      description: "Number of days and nights of hotel booking",
                    },
                    {
                      daysCount: numberOfDays,
                      nightsCount: numberOfDays - 1,
                    },
                  )}
                </div>
              </div>
            </div>
            <Divider />
            <ButtonBase
              className="flex gap-2 text-start"
              onClick={() => {
                copyToClipboardWithFeedback(
                  hotel.address,
                  intl.formatMessage({
                    defaultMessage: "Hotel address copied",
                    description:
                      "Snackbar shown after copying the hotel address",
                  }),
                );
              }}
            >
              <MapPinIcon className="size-5 shrink-0" />
              <div className="space-y-1">
                <div className="font-medium text-text-primary">
                  {intl.formatMessage({
                    defaultMessage: "Location",
                    description: "Label for the location of the hotel",
                  })}
                </div>
                <div className="flex items-center justify-between gap-1">
                  <span className="text-sm text-text-tertiary">
                    {hotel.address}
                  </span>
                  <CopyIcon className="size-5" />
                </div>
              </div>
            </ButtonBase>
            <Divider />
            <ButtonBase
              className="flex items-center justify-between"
              onClick={() => {
                setTravellersDrawerVisible(true);
              }}
            >
              <div className="flex gap-2">
                <IconTravellers className="size-5 shrink-0" />
                <div className="space-y-1 text-start">
                  <div className="font-medium text-text-primary">
                    {intl.formatMessage({
                      defaultMessage: "Travellers",
                      description: "Label of hotel booking travellers",
                    })}
                  </div>
                  <div className="text-sm text-text-tertiary">
                    {intl.formatMessage(
                      {
                        defaultMessage:
                          "{travellersCount, plural, one {# Guest} other {# Guests}}",
                        description: "Number of guests in hotel booking ",
                      },
                      {
                        travellersCount: reservation.travelers.length,
                      },
                    )}
                  </div>
                </div>
              </div>
              <CaretRightIcon className="size-5 shrink-0" />
            </ButtonBase>
            <Divider />
            <div className="flex gap-2">
              <CheckCircleIcon className="size-5 shrink-0" />
              <div className="space-y-1">
                <div className="font-medium text-text-primary">
                  {intl.formatMessage({
                    defaultMessage: "Confirmation number",
                    description: "Label for hotel booking confirmation number",
                  })}
                </div>
                <div className="text-sm text-text-tertiary">
                  {reservation.reference.external}
                </div>
              </div>
            </div>
          </div>

          {orderData?.reservations.map((reservation) => (
            <ReservationDocumentList
              key={reservation.id}
              reservationId={reservation.reservationId}
            />
          ))}
        </div>
      </div>
      <SwipeableDrawer
        onClose={() => setTravellersDrawerVisible(false)}
        open={travellersDrawerVisible && !selectedTraveller}
        backgroundColor="background"
        hasFullHeight
      >
        <DrawerTopBar
          onCloseClick={() => setTravellersDrawerVisible(false)}
          title={intl.formatMessage({
            defaultMessage: "Travellers",
            description:
              "Travellers drawer title on hotel booking success screen",
          })}
        />
        <DrawerContent>
          <ul className="section-card flex flex-col gap-4 p-4">
            {travellers.map((traveller, idx) => (
              <li key={`${traveller.firstName}${traveller.lastName}${idx}`}>
                <ButtonBase
                  onClick={() => setSelectedTraveller(traveller)}
                  className="flex w-full items-center justify-between gap-1"
                >
                  <IconTraveller className="mt-0.5 mb-auto size-6 shrink-0" />
                  <div className="text-start">
                    <div className="text-text-primary">
                      {traveller.firstName} {traveller.lastName}
                    </div>
                    <div className="space-x-2">
                      <Chip
                        color="neutral-surface"
                        variant="outlined"
                        shape="rounded"
                        label={intl.formatMessage(
                          {
                            defaultMessage: "Age: {age}",
                            description:
                              "Label for traveller's age at hotel booking summary page",
                          },
                          { age: getAgeFromDateOfBirth(traveller.birthDate) },
                        )}
                      />
                      {traveller.id === leadTravellerId ? (
                        <Chip
                          color="accent-soft"
                          shape="rounded"
                          label={intl.formatMessage({
                            defaultMessage: "Main traveller",
                            description:
                              "Badge or label identifying the primary / main traveller in a booking",
                          })}
                        />
                      ) : null}
                    </div>
                  </div>
                  <CaretRightIcon className="m-2 ms-auto size-4 shrink-0" />
                </ButtonBase>
              </li>
            ))}
          </ul>
        </DrawerContent>
      </SwipeableDrawer>
      <SwipeableDrawer
        open={selectedTraveller !== null}
        onClose={() => setSelectedTraveller(null)}
        backgroundColor="background"
        hasFullHeight
      >
        <DrawerTopBar
          title={intl.formatMessage({
            defaultMessage: "Traveller details",
            description: "Title for the traveller details drawer",
          })}
          onCloseClick={() => setSelectedTraveller(null)}
        />

        <DrawerContent>
          {selectedTraveller ? (
            <TravellerDetailsCard
              hidePassportDetails
              traveller={selectedTraveller}
            />
          ) : null}
        </DrawerContent>
      </SwipeableDrawer>
    </>
  );
};
